import React, { useState, useEffect, useRef, useCallback } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { bodyRequest, getRequest, deleteRequest, pathname } from '@helpers/javascript/javascript'
import { convertKeysToSnake } from "@react/helpers/helpers"
import Combobox               from "@shared/combobox"
import SharedFlatpickr        from "@shared/flatpickr"

const atHour = (date, hours, minutes = 0) => {
  const d = new Date(date); d.setHours(hours, minutes, 0, 0); return d
}
const addDays = (date, days) => {
  const d = new Date(date); d.setDate(d.getDate() + days); return d
}
const mondayOf = date => addDays(date, -((new Date(date).getDay() + 6) % 7))
const sameDay  = (a, b) => new Date(a).toDateString() === new Date(b).toDateString()
const iso      = date => new Date(date).toISOString()

const AbsenceModal = ({ prefill, onClose, onSaved }) => {
  const { absenceId, technicianId, startDate, endDate, onCallDuty, anchorDate, allDay, reason, categoryId } = prefill

  const anchor = anchorDate ? new Date(anchorDate) : new Date()

  const blank = () => ({
    userId:     technicianId ? String(technicianId) : '',
    // Default to a full day; an Alt+drag over hours keeps its own range.
    allDay:     startDate ? !!allDay : true,
    startDate:  startDate || iso(atHour(anchor, 0)),
    endDate:    endDate   || iso(atHour(anchor, 23, 59)),
    reason:     reason || (onCallDuty ? I18n.t("users.absences.on_call_duty") : ''),
    categoryId: categoryId ? String(categoryId) : '',
  })

  const [editedId, setEditedId] = useState(absenceId || null)
  const editing = !!editedId

  const [form,    setForm]    = useState(blank)
  const [extra,   setExtra]   = useState({ allTechnicians: false, recurrent: false, interval: 1, frequency: null, maxEvents: 5 })
  const [options, setOptions] = useState(null)   // technicians, categories, frequencies…
  const [preview, setPreview] = useState(null)   // conflicts on the requested period
  const [history, setHistory] = useState(null)   // every absence of that technician
  const [tab,     setTab]     = useState('upcoming')
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState(null)
  const formRef = useRef()

  const startAt = new Date(form.startDate)
  const endAt   = new Date(form.endDate)
  const patch   = changes => setForm(prev => ({ ...prev, ...changes }))

  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose])

  // ── Form options (technicians, categories, frequencies) ──
  useEffect(() => {
    getRequest(pathname({ path: '/absences/new', format: 'json' }))
      .then(res => {
        setOptions(res)
        setExtra(prev => ({ ...prev, frequency: res.frequencies?.[0] }))
      })
      .catch(() => setError(I18n.t("users.absences.load_error")))
  }, [])

  // ── Conflicts on the requested period ──
  useEffect(() => {
    if (!form.userId) return setPreview(null)

    let cancelled = false
    const params = { user_id: form.userId, start_date: form.startDate, end_date: form.endDate }
    if (editedId) params.absence_id = editedId
    getRequest(pathname({ path: '/absences/preview', format: 'json', params }))
      .then(res => { if (!cancelled) setPreview(res) }).catch(() => {})
    return () => { cancelled = true }
  }, [form.userId, form.startDate, form.endDate, editedId])

  // ── Everything already recorded for that technician (review panel) ──
  const loadHistory = useCallback(() => {
    if (!form.userId) return setHistory(null)

    const from = addDays(new Date(), -365).toISOString().split('T')[0]
    const to   = addDays(new Date(),  730).toISOString().split('T')[0]
    getRequest(pathname({
      path: '/absences', format: 'json',
      params: { user_id: form.userId, period: `${from},${to}` }
    })).then(res => setHistory(res.absences || [])).catch(() => setHistory([]))
  }, [form.userId])

  useEffect(() => { loadHistory() }, [loadHistory])

  useEffect(() => {
    if (!editedId || history === null) return

    const edited = history.find(absence => absence.id === editedId)
    if (edited) setTab(new Date(edited.end_date) < new Date() ? 'past' : 'upcoming')
  }, [editedId, history])

  const applyPreset = preset => {
    const map = {
      today:     [atHour(anchor, 0),             atHour(anchor, 23, 59)],
      tomorrow:  [atHour(addDays(anchor, 1), 0), atHour(addDays(anchor, 1), 23, 59)],
      week:      [atHour(mondayOf(anchor), 0),   atHour(addDays(mondayOf(anchor), 4), 23, 59)],
      morning:   [atHour(anchor, 8),             atHour(anchor, 12)],
      afternoon: [atHour(anchor, 12),            atHour(anchor, 18)],
    }
    const [from, to] = map[preset]
    patch({ startDate: iso(from), endDate: iso(to), allDay: !["morning", "afternoon"].includes(preset) })
  }

  const activePreset = () => {
    if (form.allDay) {
      if (sameDay(startAt, anchor) && sameDay(endAt, anchor))                                    return "today"
      if (sameDay(startAt, addDays(anchor, 1)) && sameDay(endAt, addDays(anchor, 1)))            return "tomorrow"
      if (sameDay(startAt, mondayOf(anchor)) && sameDay(endAt, addDays(mondayOf(anchor), 4)))    return "week"
    } else {
      if (sameDay(startAt, anchor) && startAt.getHours() === 8  && endAt.getHours() === 12)      return "morning"
      if (sameDay(startAt, anchor) && startAt.getHours() === 12 && endAt.getHours() === 18)      return "afternoon"
    }
    return null
  }

  const shortDate   = date => date.toLocaleDateString(I18n.locale, { day: 'numeric', month: 'short' })
  const presetLabel = preset => {
    const isToday = sameDay(anchor, new Date())
    if (preset === "today")    return isToday ? I18n.t("users.absences.preset_today")    : shortDate(anchor)
    if (preset === "tomorrow") return isToday ? I18n.t("users.absences.preset_tomorrow") : shortDate(addDays(anchor, 1))
    return I18n.t(`users.absences.preset_${preset}`)
  }

  const technician = () => (options?.technicians || []).find(t => String(t.id) === String(form.userId))

  const summary = () => {
    const tech = technician()
    if (!tech) return null

    const opts = { weekday: 'long', day: 'numeric', month: 'long' }
    const from = startAt.toLocaleDateString(I18n.locale, opts)
    const to   = endAt.toLocaleDateString(I18n.locale, opts)
    const time = d => d.toLocaleTimeString(I18n.locale, { hour: '2-digit', minute: '2-digit' })

    if (sameDay(startAt, endAt)) {
      return form.allDay
        ? I18n.t("users.absences.summary_day",   { name: tech.name, date: from })
        : I18n.t("users.absences.summary_hours", { name: tech.name, date: from, from: time(startAt), to: time(endAt) })
    }
    return I18n.t("users.absences.summary_range", { name: tech.name, from, to })
  }

  const toggleAllDay = () => {
    const allDay = !form.allDay
    patch({
      allDay,
      startDate: iso(allDay ? atHour(startAt, 0)     : atHour(startAt, 8)),
      endDate:   iso(allDay ? atHour(endAt, 23, 59)  : atHour(endAt, 18)),
    })
  }

  const changeCategory = category => patch({
    categoryId: category.id,
    reason:     category.id && !form.reason ? category.name : form.reason,
  })

  const removeAbsence = (id, label) => {
    const question = [I18n.t("users.absences.delete_confirm"), label].filter(Boolean).join('\n\n')
    if (!window.confirm(question)) return

    deleteRequest(pathname({ path: `absences/${id}` })).then(res => {
      if (res.unprocessed) return
      setHistory(prev => (prev || []).filter(a => a.id !== id))
      setPreview(prev => prev && {
        ...prev,
        absences:      prev.absences.filter(a => a.id !== id),
        absencesTotal: Math.max(prev.absencesTotal - 1, 0),
      })
      onSaved()
      if (id === editedId) onClose()
    })
  }

  const startEditing = absence => {
    setEditedId(absence.id)
    setError(null)
    setForm({
      userId:     String(absence.user_id || form.userId),
      allDay:     !!absence.all_day,
      startDate:  absence.start_date,
      endDate:    absence.end_date,
      reason:     absence.reason || '',
      categoryId: absence.category ? String(absence.category.id) : '',
    })
  }

  const stopEditing = () => {
    setEditedId(null)
    setError(null)
    patch({ reason: onCallDuty ? I18n.t("users.absences.on_call_duty") : '', categoryId: '' })
  }

  const submit = keepOpen => {
    if (!formRef.current.reportValidity()) return
    if (!editing && extra.allTechnicians &&
        !window.confirm(I18n.t("users.absences.all_technicians_confirm", { count: options.technicians.length }))) return

    setSaving(true)
    setError(null)
    const body = { absence: convertKeysToSnake({ ...form, onCallDuty: !!onCallDuty }) }
    const request = editing
      ? bodyRequest({ url: pathname({ path: `/absences/${editedId}`, format: 'json' }), body, method: 'PATCH' })
      : bodyRequest({ url: pathname({ path: '/absences' }), body: { ...body, ...convertKeysToSnake(extra) } })

    request.then(() => {
      onSaved()
      setSaving(false)
      if (!keepOpen) return onClose()
      loadHistory()
      patch({ reason: onCallDuty ? I18n.t("users.absences.on_call_duty") : '', categoryId: '' })
    }).catch(() => {
      setSaving(false)
      setError(I18n.t("users.absences.save_error"))
    })
  }

  const PRESETS = onCallDuty ? ["today", "tomorrow", "week"] : ["today", "tomorrow", "week", "morning", "afternoon"]

  const now      = new Date()
  const upcoming = (history || []).filter(a => new Date(a.end_date) >= now)
  const past     = (history || []).filter(a => new Date(a.end_date) <  now)
                                  .sort((a, b) => new Date(b.end_date) - new Date(a.end_date))
  const shown    = tab === 'upcoming' ? upcoming : past

  const workorderCount = preview?.workordersTotal || 0
  const mandatory      = !!options?.mandatoryCategory

  return (
    <div className="abs-modal__backdrop" onClick={onClose}>
      <div className="abs-modal" onClick={e => e.stopPropagation()}>

        <header className="abs-modal__header">
          <div>
            <div className="abs-modal__eyebrow">
              {onCallDuty ? I18n.t("users.absences.on_call_duty") : I18n.t("users.absences.title")}
            </div>
            <h2 className="abs-modal__title">
              {editing
                ? I18n.t("users.absences.edit")
                : onCallDuty ? I18n.t("users.absences.new_on_call_duty") : I18n.t("users.absences.new")}
            </h2>
          </div>
          <button type="button" className="abs-modal__close" onClick={onClose} title={I18n.t('button.close')}>
            <FontAwesomeIcon icon="xmark" />
          </button>
        </header>

        {/* Combobox resolves its preselected option on first render only, so the
            form waits for the technician list before mounting. */}
        {!options
          ? <div className="abs-modal__loading">{error || '…'}</div>
          : <>
        <div className="abs-modal__body">
          {/* ── Left: record a new absence ─────────────────────────────── */}
          <form key={editedId || 'new'} ref={formRef} className="abs-form">
            <div className="abs-field">
              <label className="abs-label">{I18n.t("workorders.technician")} <span className="abs-req">*</span></label>
              <Combobox
                options         = {options?.technicians || []}
                onSelectElement = {tech => patch({ userId: tech?.id ? String(tech.id) : '' })}
                placeholder     = {I18n.t("users.absences.select_user")}
                value           = {form.userId}
                required
              />
            </div>

            <div className="abs-field">
              <label className="abs-label">{I18n.t("users.absences.period")}</label>
              <div className="abs-presets">
                {PRESETS.map(preset => (
                  <button
                    type      = "button"
                    key       = {preset}
                    className = {`abs-preset${activePreset() === preset ? ' is-active' : ''}`}
                    onClick   = {() => applyPreset(preset)}
                  >
                    {presetLabel(preset)}
                  </button>
                ))}
              </div>
            </div>

            <div className="abs-row">
              <div className="abs-field">
                <label className="abs-label">{I18n.t("users.absences.start_card")} <span className="abs-req">*</span></label>
                <SharedFlatpickr
                  key       = {`start-${form.allDay}`}
                  className = "abs-input text-center"
                  onChange  = {d => patch({ startDate: iso(d[0]) })}
                  onBlur    = {d => patch({ startDate: iso(d[0]) })}
                  value     = {startAt}
                  options   = {{ locale: I18n.locale, enableTime: !form.allDay, dateFormat: form.allDay ? "d/m/Y" : "d/m/Y - H:i" }}
                />
              </div>
              <div className="abs-field">
                <label className="abs-label">{I18n.t("users.absences.end_card")} <span className="abs-req">*</span></label>
                <SharedFlatpickr
                  key       = {`end-${form.allDay}`}
                  className = "abs-input text-center"
                  onChange  = {d => patch({ endDate: iso(d[0]) })}
                  value     = {endAt}
                  options   = {{ locale: I18n.locale, enableTime: !form.allDay, dateFormat: form.allDay ? "d/m/Y" : "d/m/Y - H:i", minDate: startAt }}
                />
              </div>
            </div>

            <label className="abs-check">
              <input type="checkbox" checked={form.allDay} onChange={toggleAllDay} />
              <span>{I18n.t("users.absences.all_day")}</span>
            </label>

            {summary() &&
              <div className="abs-summary">
                <FontAwesomeIcon icon="calendar-check" />
                <span>
                  {summary()}
                  {preview?.workingDays > 1 &&
                    <b> · {I18n.t("users.absences.working_days", { count: preview.workingDays })}</b>}
                </span>
              </div>}

            {workorderCount > 0 &&
              <div className="abs-alert">
                <div className="abs-alert__head">
                  <FontAwesomeIcon icon="triangle-exclamation" />
                  {I18n.t("users.absences.conflict_workorders", { count: workorderCount })}
                </div>
                <ul>
                  {preview.workorders.map(wo => <li key={wo.id}><b>{wo.when}</b> — {wo.name} {wo.title}</li>)}
                  {workorderCount > preview.workorders.length &&
                    <li className="abs-muted">{I18n.t("users.absences.and_more", { count: workorderCount - preview.workorders.length })}</li>}
                </ul>
              </div>}

            {!onCallDuty && (options?.categories || []).length > 0 &&
              <div className="abs-field">
                <label className="abs-label">
                  {I18n.t("categories.category")} {mandatory && <span className="abs-req">*</span>}
                </label>
                <Combobox
                  prompt          = {I18n.t('categories.select')}
                  options         = {options.categories}
                  onSelectElement = {changeCategory}
                  value           = {form.categoryId}
                  required        = {mandatory}
                />
              </div>}

            <div className="abs-field">
              <label className="abs-label">
                {I18n.t("users.absences.reason")} {!mandatory && <span className="abs-req">*</span>}
              </label>
              <input
                type        = "text"
                name        = "reason"
                className   = "abs-input"
                value       = {form.reason}
                onChange    = {e => patch({ reason: e.target.value })}
                placeholder = {I18n.t("users.absences.reason_placeholder")}
                required    = {!mandatory}
              />
            </div>

            {!editing &&
            <div className="abs-options">
              {!onCallDuty &&
                <label className="abs-check">
                  <input
                    type     = "checkbox"
                    checked  = {extra.allTechnicians}
                    onChange = {() => setExtra(p => ({ ...p, allTechnicians: !p.allTechnicians }))}
                  />
                  <span>{I18n.t("users.absences.all_technicians_count", { count: options?.technicians?.length || 0 })}</span>
                </label>}

              <label className="abs-check">
                <input
                  type     = "checkbox"
                  checked  = {extra.recurrent}
                  onChange = {() => setExtra(p => ({ ...p, recurrent: !p.recurrent }))}
                />
                <span>{I18n.t("users.absences.repeat")}</span>
              </label>

              {extra.recurrent &&
                <div className="abs-recurrence">
                  <span>{I18n.t("users.absences.repeat_every")}</span>
                  <select className="abs-input abs-input--xs" value={extra.interval}
                          onChange={e => setExtra(p => ({ ...p, interval: e.target.value }))}>
                    {[...Array(12)].map((_, i) => i + 1).map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                  <select className="abs-input abs-input--sm" value={extra.frequency || ''}
                          onChange={e => setExtra(p => ({ ...p, frequency: e.target.value }))}>
                    {(options?.frequencies || []).map((frequency, index) =>
                      <option key={frequency} value={frequency}>{I18n.t("maintenance.frequencies.singular")[index]}</option>)}
                  </select>
                  <span>{I18n.t("users.absences.max_events")}</span>
                  <input
                    type      = "number" min={1} step={1}
                    className = "abs-input abs-input--xs"
                    value     = {extra.maxEvents}
                    onChange  = {e => setExtra(p => ({ ...p, maxEvents: e.target.value }))}
                  />
                </div>}
            </div>}

            {error && <div className="abs-error">{error}</div>}
          </form>

          {/* ── Right: review what is already recorded ─────────────────── */}
          <aside className="abs-side">
            <div className="abs-side__head">
              <h3>{technician() ? technician().name : I18n.t("users.absences.title")}</h3>
              <div className="abs-tabs">
                <button type="button" className={`abs-tab${tab === 'upcoming' ? ' is-active' : ''}`} onClick={() => setTab('upcoming')}>
                  {I18n.t("users.absences.tab_upcoming")} <span className="abs-count">{upcoming.length}</span>
                </button>
                <button
                  type      = "button"
                  className = {`abs-tab${tab === 'past' ? ' is-active' : ''}`}
                  title     = {I18n.t("users.absences.past_window")}
                  onClick   = {() => setTab('past')}
                >
                  {I18n.t("users.absences.tab_past")} <span className="abs-count">{past.length}</span>
                </button>
              </div>
            </div>

            <div className="abs-side__list">
              {/* The list is a rolling window: say so rather than let the
                  dispatcher wonder where the older entries went. */}
              {form.userId && tab === 'past' && history !== null &&
                <p className="abs-side__window">{I18n.t("users.absences.past_window")}</p>}

              {!form.userId && <p className="abs-side__empty">{I18n.t("users.absences.pick_technician_first")}</p>}
              {form.userId && history === null && <p className="abs-side__empty">…</p>}
              {form.userId && history !== null && shown.length === 0 &&
                <p className="abs-side__empty">{I18n.t("users.absences.none_recorded")}</p>}

              {shown.map(absence => (
                <div key={absence.id}
                     className={`abs-item${absence.on_call_duty ? ' abs-item--duty' : ''}${absence.id === editedId ? ' is-edited' : ''}`}>
                  <div className="abs-item__main">
                    <div className="abs-item__when">{absence.show_start_date} → {absence.show_end_date}</div>
                    <div className="abs-item__reason">{absence.reason}</div>
                  </div>
                  {absence.category &&
                    <span className="abs-item__cat"
                          style={{ backgroundColor: absence.category.background, color: absence.category.color }}>
                      {absence.category.name}
                    </span>}
                  {absence.can_update &&
                    <button type="button" className="abs-item__edit" title={I18n.t('users.absences.edit')}
                            onClick={() => startEditing(absence)}>
                      <FontAwesomeIcon icon="pen" />
                    </button>}
                  {absence.can_update &&
                    <button type="button" className="abs-item__del" title={I18n.t('button.delete')}
                            onClick={() => removeAbsence(absence.id, `${absence.show_start_date} → ${absence.show_end_date}${absence.reason ? ` · ${absence.reason}` : ''}`)}>
                      <FontAwesomeIcon icon="trash" />
                    </button>}
                </div>
              ))}
            </div>
          </aside>
        </div>

        <footer className="abs-modal__footer">
          <button type="button" className="abs-btn" onClick={onClose}>{I18n.t('button.cancel')}</button>
          <div className="abs-modal__actions">
            {editing ? <>
              <button type="button" className="abs-btn abs-btn--danger" disabled={saving}
                      onClick={() => removeAbsence(editedId, summary())}>
                <FontAwesomeIcon icon="trash" /> {I18n.t('button.delete')}
              </button>
              <button type="button" className="abs-btn" disabled={saving} onClick={stopEditing}>
                <FontAwesomeIcon icon="plus" /> {I18n.t('button.add')}
              </button>
              <button type="button" className="abs-btn abs-btn--primary" disabled={saving} onClick={() => submit(false)}>
                <FontAwesomeIcon icon="check" /> {I18n.t('button.save')}
              </button>
            </> : <>
              <button type="button" className="abs-btn" disabled={saving} onClick={() => submit(true)}>
                {I18n.t('button.save_and_create')}
              </button>
              <button type="button" className="abs-btn abs-btn--primary" disabled={saving} onClick={() => submit(false)}>
                <FontAwesomeIcon icon="check" /> {I18n.t('button.add')}
              </button>
            </>}
          </div>
        </footer>
        </>}
      </div>
    </div>
  )
}

export default AbsenceModal
